import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, CheckCircle2, FileSignature, Megaphone, Plus, Search, ShieldCheck } from 'lucide-react'
import { Trail } from '../navigation/Trail.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { Modal, apiGet, adminMutate, useToast, usePermissions } from '../admin/primitives.js'
import { Combobox, SegmentedControl, type ComboItem } from '../admin/pickers.js'
import { Field, TextInput } from '../admin/form.js'
import { useMenuPersonne } from './menu-personne.js'
import './rh.css'
import './presence.css'

/**
 * BORNE NATIVE — REGISTRE DES SIGNATURES et ADMINISTRATION DES ANNONCES.
 *
 * Deux principes portés jusque dans l'écran :
 *  · une preuve se VÉRIFIE. Le détail affiche le tracé, les deux horodatages
 *    (appareil et serveur), la borne, et ce qui a été signé — figé.
 *  · publier n'est pas rédiger. Le bouton de publication est séparé, il dit ce
 *    qu'il fige, et il n'apparaît qu'avec le droit correspondant.
 */

type Action = 'CLOCK_IN' | 'BREAK_START' | 'BREAK_END' | 'CLOCK_OUT' | 'ANNOUNCEMENT_APPROVAL'

const ACTION_LABEL: Record<Action, string> = {
  CLOCK_IN: 'Prise de poste',
  BREAK_START: 'Départ en pause',
  BREAK_END: 'Retour de pause',
  CLOCK_OUT: 'Fin de service',
  ANNOUNCEMENT_APPROVAL: 'Approbation d’annonce',
}

interface SignatureRow {
  id: string; person_id: string; display_name: string; position_name: string | null
  action: Action; signed_device_at: string; received_at: string
  station_id: string | null; station_name: string | null
  announcement_title: string | null; event_name: string | null; strokes_sha256: string
}
interface Traits { w: number; h: number; s: Array<Array<[number, number]>> }
interface SignatureDetail extends SignatureRow {
  strokes: Traits; snapshot: Record<string, unknown>; snapshot_sha256: string; integrity_ok: boolean
}

const PARIS = 'Europe/Paris'
const dateHeure = (iso: string): string =>
  new Intl.DateTimeFormat('fr-FR', { dateStyle: 'short', timeStyle: 'medium', timeZone: PARIS }).format(new Date(iso))

/**
 * Rend le tracé manuscrit.
 *
 * Les points sont normalisés entre 0 et 1 : on les remet à l'échelle du cadre.
 * C'est tout l'intérêt d'avoir stocké un VECTEUR plutôt qu'une image — net à
 * n'importe quelle taille, et dix fois plus léger.
 */
function Trace({ traits }: { traits: Traits }) {
  const L = 500
  const H = Math.max(60, Math.round((L * traits.h) / Math.max(traits.w, 0.001)))
  const chemins = traits.s.map((trait) =>
    trait.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${(x * L).toFixed(1)},${(y * H).toFixed(1)}`).join(' '))
  return (
    <svg className="sg-trace" viewBox={`0 0 ${L} ${H}`} role="img" aria-label="Tracé manuscrit de la signature">
      {chemins.map((d, i) => (
        <path key={i} d={d} fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" />
      ))}
    </svg>
  )
}

export function HrSignaturesPage() {
  const perms = usePermissions()
  const peut = (k: string): boolean => perms.permissions.includes(k)
  const menu = useMenuPersonne()
  const [rows, setRows] = useState<SignatureRow[]>([])
  const [total, setTotal] = useState(0)
  const [action, setAction] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [ouvert, setOuvert] = useState<SignatureDetail | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  const charger = useCallback(async () => {
    try {
      const p = new URLSearchParams()
      if (action) p.set('action', action)
      const r = await apiGet<{ rows: SignatureRow[]; total: number }>(`/api/v1/hr/signatures?${p.toString()}`)
      setRows(r.rows); setTotal(r.total); setErreur(null)
    } catch { setErreur('Impossible de lire le registre des signatures.') }
  }, [action])
  useEffect(() => { void charger() }, [charger])

  const filtrees = useMemo(() => {
    const t = q.trim().toLowerCase()
    return t ? rows.filter((r) => r.display_name.toLowerCase().includes(t)) : rows
  }, [rows, q])

  const ouvrir = async (id: string) => {
    try { setOuvert(await apiGet<SignatureDetail>(`/api/v1/hr/signatures/${id}`)) }
    catch { setErreur('Cette preuve n’a pas pu être ouverte.') }
  }

  if (!peut('time.signature.read')) {
    return <div className="gd-panel adm-note">Vous n’avez pas le droit de consulter les signatures.</div>
  }

  const actions: ComboItem[] = (Object.keys(ACTION_LABEL) as Action[]).map((a) => ({ value: a, label: ACTION_LABEL[a] }))

  return (
    <div className="lc-page">
      <SmartBack />
      <Trail />
      <header className="hub-hero">
        <p className="hub-hero__kicker"><FileSignature size={13} aria-hidden="true" /> Temps</p>
        <h1>Signatures</h1>
        <p className="hub-hero__sub">
          Les preuves manuscrites recueillies à la borne. Chacune est rattachée à une action précise
          et conserve ce qui était affiché au moment de signer.
        </p>
      </header>

      {erreur ? <div className="gd-panel adm-note adm-note--warn">{erreur}</div> : null}

      <div className="pr-filtres">
        <div className="pr-recherche">
          <Search size={14} aria-hidden="true" />
          <TextInput value={q} onChange={setQ} placeholder="Rechercher un salarié" ariaLabel="Rechercher un salarié" />
        </div>
        <Combobox items={actions} value={action} onChange={setAction} placeholder="Toutes les actions" ariaLabel="Filtrer par type d’action" />
        <span className="pr-fraicheur">{total} preuve{total > 1 ? 's' : ''}</span>
      </div>

      {filtrees.length === 0 ? (
        <div className="gd-panel adm-note">Aucune signature enregistrée pour ces critères.</div>
      ) : (
        <ul className="pr-liste">
          {filtrees.map((r) => (
            <li key={r.id} className="pr-carte" {...menu.personne({ id: r.person_id, nom: r.display_name })}>
              <div className="pr-carte__id">
                <span className="pr-nom">{r.display_name}</span>
                <span className="pr-meta">{r.position_name ?? '—'}</span>
              </div>
              <div className="pr-carte__etat">
                <span className="pr-tag">{ACTION_LABEL[r.action]}</span>
                {r.announcement_title ? <span className="pr-tag pr-tag--violet">{r.announcement_title}</span> : null}
              </div>
              <div className="pr-carte__horaires">
                <span><b>Signé</b> {dateHeure(r.signed_device_at)}</span>
                <span><b>Borne</b> {r.station_name ?? '—'}</span>
              </div>
              <div className="pr-carte__chiffres">
                <button type="button" className="lc-btn lc-btn--ghost" onClick={() => void ouvrir(r.id)}>
                  Ouvrir la preuve
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal open={!!ouvert} onClose={() => setOuvert(null)} ariaLabel="Preuve de signature" width={640}>
        {ouvert ? (
          <div className="sg-preuve">
            <h3>Preuve de signature</h3>
            <Trace traits={ouvert.strokes} />
            <p className={`sg-integrite sg-integrite--${ouvert.integrity_ok ? 'ok' : 'ko'}`}>
              {ouvert.integrity_ok
                ? <><ShieldCheck size={14} aria-hidden="true" /> Le tracé et l’instantané correspondent à leurs empreintes.</>
                : <><AlertTriangle size={14} aria-hidden="true" /> Empreinte non concordante — cette preuve a été altérée.</>}
            </p>
            <dl className="sg-champs">
              <dt>Salarié</dt><dd>{ouvert.display_name}</dd>
              <dt>Action</dt><dd>{ACTION_LABEL[ouvert.action]}</dd>
              <dt>Heure appareil</dt><dd>{dateHeure(ouvert.signed_device_at)}</dd>
              <dt>Reçu par le serveur</dt><dd>{dateHeure(ouvert.received_at)}</dd>
              <dt>Borne</dt><dd>{ouvert.station_name ?? '—'}</dd>
              <dt>Texte approuvé</dt><dd>{String(ouvert.snapshot['consent_text'] ?? '—')}</dd>
              <dt>Empreinte du tracé</dt><dd><code>{ouvert.strokes_sha256.slice(0, 32)}…</code></dd>
              <dt>Empreinte de l’instantané</dt><dd><code>{ouvert.snapshot_sha256.slice(0, 32)}…</code></dd>
            </dl>
          </div>
        ) : null}
      </Modal>
    </div>
  )
}

/* ═══════════════════════════ ANNONCES ═══════════════════════════ */

type Priorite = 'INFO' | 'IMPORTANT' | 'REQUIRES_ACTION'
type Ack = 'NONE' | 'ACKNOWLEDGE' | 'SIGNATURE'

interface AnnonceRow {
  announcement_id: string; version_id: string; version: number
  title: string; priority: Priorite; ack_mode: Ack
  status: 'DRAFT' | 'PUBLISHED' | 'ENDED' | 'ARCHIVED'
  published_at: string | null; author: string | null
  stats: { recipients: number; opened: number; acknowledged: number; signed: number; refused: number; not_seen: number }
}
interface Refus {
  receipt_id: string; person_id: string; display_name: string
  announcement_id: string; title: string; refused_at: string; refusal_reason: string | null
}

const STATUT: Record<AnnonceRow['status'], string> = {
  DRAFT: 'Brouillon', PUBLISHED: 'Publiée', ENDED: 'Terminée', ARCHIVED: 'Archivée',
}
const ACK_LABEL: Record<Ack, string> = {
  NONE: 'Information', ACKNOWLEDGE: 'Accusé de lecture', SIGNATURE: 'Signature obligatoire',
}

export function HrAnnoncesPage() {
  const perms = usePermissions()
  const peut = (k: string): boolean => perms.permissions.includes(k)
  const toast = useToast()
  const menu = useMenuPersonne()
  const [rows, setRows] = useState<AnnonceRow[]>([])
  const [refus, setRefus] = useState<Refus[]>([])
  const [creation, setCreation] = useState(false)
  const [titre, setTitre] = useState('')
  const [corps, setCorps] = useState('')
  const [mode, setMode] = useState<Ack>('NONE')
  const [priorite, setPriorite] = useState<Priorite>('INFO')
  const [engagement, setEngagement] = useState('J’ai lu et approuvé cette annonce.')

  const charger = useCallback(async () => {
    const [a, r] = await Promise.all([
      apiGet<{ announcements: AnnonceRow[] }>('/api/v1/hr/announcements'),
      apiGet<{ refusals: Refus[] }>('/api/v1/hr/announcements/refusals'),
    ])
    setRows(a.announcements); setRefus(r.refusals)
  }, [])
  useEffect(() => { void charger() }, [charger])

  const creer = async () => {
    const { data: r } = await adminMutate<{ error?: string; message?: string }>('POST', '/api/v1/hr/announcements', {
      title: titre, body: corps, priority: priorite, ack_mode: mode,
      consent_text: mode === 'SIGNATURE' ? engagement : null,
      targets: [{ target_type: 'ALL', target_id: null }],
    })
    if (r.error) { toast('error', r.message ?? 'Création refusée.'); return }
    toast('ok', 'Annonce créée en brouillon.')
    setCreation(false); setTitre(''); setCorps('')
    await charger()
  }

  const publier = async (versionId: string, titreAnnonce: string) => {
    const { data: r } = await adminMutate<{ error?: string; message?: string; recipients?: number }>('POST', `/api/v1/hr/announcements/versions/${versionId}/publish`)
    if (r.error) { toast('error', r.message ?? 'Publication refusée.'); return }
    toast('ok', `« ${titreAnnonce} » publiée — ${r.recipients} destinataire${(r.recipients ?? 0) > 1 ? 's' : ''} figé${(r.recipients ?? 0) > 1 ? 's' : ''}.`)
    await charger()
  }

  const traiter = async (receiptId: string) => {
    const { data: r } = await adminMutate<{ error?: string; message?: string }>('POST', `/api/v1/hr/announcements/refusals/${receiptId}/resolve`)
    if (r.error) { toast('error', r.message ?? 'Traitement impossible.'); return }
    toast('ok', 'Refus pris en charge — il reste consigné.')
    await charger()
  }

  if (!peut('hr.announcement.read')) {
    return <div className="gd-panel adm-note">Vous n’avez pas le droit de consulter les annonces.</div>
  }

  return (
    <div className="lc-page">
      <SmartBack />
      <Trail />
      <header className="hub-hero">
        <p className="hub-hero__kicker"><Megaphone size={13} aria-hidden="true" /> Temps</p>
        <h1>Annonces</h1>
        <p className="hub-hero__sub">
          Les messages destinés au personnel, y compris aux salariés sans compte Gestion.
          Publier fige le texte et gèle la liste des destinataires.
        </p>
      </header>

      {refus.length > 0 ? (
        <section className="gd-panel adm-note adm-note--warn">
          <h2><AlertTriangle size={15} aria-hidden="true" /> Refus de signature à traiter</h2>
          <ul className="pr-liste">
            {refus.map((r) => (
              <li key={r.receipt_id} className="pr-carte" {...menu.personne({ id: r.person_id, nom: r.display_name })}>
                <div className="pr-carte__id">
                  <span className="pr-nom an-refus">{r.display_name}</span>
                  <span className="pr-meta">a refusé de signer « {r.title} »</span>
                </div>
                <div className="pr-carte__horaires"><span>{dateHeure(r.refused_at)}</span></div>
                <div className="pr-carte__chiffres">
                  <span className="pr-meta">{r.refusal_reason ?? 'Aucun motif indiqué.'}</span>
                </div>
                <div className="pr-carte__chiffres">
                  {peut('hr.announcement.manage') ? (
                    <button type="button" className="lc-btn lc-btn--ghost" onClick={() => void traiter(r.receipt_id)}>
                      <CheckCircle2 size={14} aria-hidden="true" /> Marquer traité
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {peut('hr.announcement.manage') ? (
        <div className="pr-barre">
          <span />
          <button type="button" className="lc-btn" onClick={() => setCreation(true)}>
            <Plus size={14} aria-hidden="true" /> Nouvelle annonce
          </button>
        </div>
      ) : null}

      {rows.length === 0 ? (
        <div className="gd-panel adm-note">Aucune annonce pour le moment.</div>
      ) : (
        <ul className="pr-liste">
          {rows.map((a) => (
            <li key={a.announcement_id} className="pr-carte">
              <div className="pr-carte__id">
                <span className="pr-nom">{a.title}</span>
                <span className="pr-meta">
                  v{a.version} · {ACK_LABEL[a.ack_mode]}{a.author ? ` · ${a.author}` : ''}
                </span>
              </div>
              <div className="pr-carte__etat">
                <span className="pr-tag">{STATUT[a.status]}</span>
                {a.ack_mode === 'SIGNATURE' ? <span className="pr-tag pr-tag--violet">Bloque le badgeage</span> : null}
              </div>
              <div className="an-stats">
                <span><b>{a.stats.recipients}</b> destinataires</span>
                <span><b>{a.stats.opened}</b> vues</span>
                {a.ack_mode === 'SIGNATURE' ? <span><b>{a.stats.signed}</b> signées</span> : null}
                {a.stats.refused > 0 ? <span className="an-refus"><b>{a.stats.refused}</b> refus</span> : null}
                <span><b>{a.stats.not_seen}</b> non vues</span>
              </div>
              <div className="pr-carte__chiffres">
                {a.status === 'DRAFT' && peut('hr.announcement.publish') ? (
                  <button type="button" className="lc-btn" onClick={() => void publier(a.version_id, a.title)}>
                    Publier
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal open={creation} onClose={() => setCreation(false)} ariaLabel="Nouvelle annonce" width={640}>
        <h3>Nouvelle annonce</h3>
        <Field label="Titre">
          <TextInput value={titre} onChange={setTitre} maxLength={160} placeholder="Nouvelle procédure sécurité" />
        </Field>
        <Field label="Texte">
          <TextInput value={corps} onChange={setCorps} maxLength={20_000} placeholder="Ce que le personnel doit savoir." />
        </Field>
        <Field label="Priorité">
          <SegmentedControl<Priorite> value={priorite} onChange={setPriorite}
            options={[{ value: 'INFO', label: 'Information' }, { value: 'IMPORTANT', label: 'Important' }, { value: 'REQUIRES_ACTION', label: 'Action requise' }]}
            ariaLabel="Priorité de l’annonce" />
        </Field>
        <Field label="Ce qu’on attend du salarié"
          helper="Une annonce à signer bloque le badgeage tant que la personne n’a pas signé OU refusé. Un refus débloque aussi : il n’empêche jamais de pointer.">
          <SegmentedControl<Ack> value={mode} onChange={setMode}
            options={[{ value: 'NONE', label: 'Rien' }, { value: 'ACKNOWLEDGE', label: 'Accusé de lecture' }, { value: 'SIGNATURE', label: 'Signature' }]}
            ariaLabel="Mode d’acquittement" />
        </Field>
        {mode === 'SIGNATURE' ? (
          <Field label="Phrase d’engagement" helper="Elle est affichée au-dessus du tracé et figée dans la preuve.">
            <TextInput value={engagement} onChange={setEngagement} maxLength={300} />
          </Field>
        ) : null}
        <div className="lc-modal__pied">
          <button type="button" className="lc-btn lc-btn--ghost" onClick={() => setCreation(false)}>Annuler</button>
          <button type="button" className="lc-btn" onClick={() => void creer()}>Créer le brouillon</button>
        </div>
      </Modal>
    </div>
  )
}
